import { useDispatch, useSelector } from 'react-redux';
import { PiggyBank } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

import {
  PlanCardHeaderSyled,
  CarrierLogoBoxStyled,
} from 'enrollment/features/Plans/PlanCard/PlanCardStyled';
import './index.css';
import { ContributionElection } from './ContributionElection';
import {
  ContributionAmountResponseDto,
  useContributionAmountQuery,
} from 'enrollment/queries/useContributionAmountQuery';
import {
  HSAContributionsResponseDto,
  // GET_V3_CONTRIBUTION_AMOUNT_QUERY_KEY,
  useV3ContributionAmountQuery,
} from 'enrollment/queries/useV3ContributionAmountQuery';
import { useConfigurationQuery } from 'theSource/queries/useConfigurationQuery';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import {
  BenefitDto,
  BenefitPlanDto,
  CoverageDto,
  PlanRiderDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import {
  selectLifeEventCompleted,
  selectPerPayPeriodText,
  updateHSAContributionResponse,
  resetHsaInitalResposne,
} from 'enrollment/services';
import { LoaderOverlay } from '../LoaderOverlay';
import {
  AdditionalPlanElectionDataDto,
  ElectRequestDto,
} from 'enrollment/features/Plans/PlanCard/types';
import { buildElectionParams } from 'enrollment/features/Plans/PlanCard/utils';
import { DecisionIQFinancialBanner } from './components';
import {
  ANNUAL_METHOD,
  valueWithinBounds,
  isHSANewExperienceEnabled,
} from './utils';
import { EsignatureModal } from 'enrollment/features/Plans/EsignatureModal';
import { RiderBenefits } from '../RiderBenefits';
import { HsaContribution } from './components/NewHsaContribution/HsaContribution';
import { useCompanySettingsQuery } from 'theSource/queries';
import { PsRadioTuner } from 'stencilSetup';
import { CategoryCoverageDto } from 'enrollment/queries/useBenefitCategoryQuery/types';

interface ContributionProps {
  benefit: BenefitDto;
  plan: BenefitPlanDto;
  coverage: CategoryCoverageDto | CoverageDto | undefined;
  selectedDependentIds: number[];
  updateElection: (params: ElectRequestDto) => void;
  showEsignatureModal?: boolean;
  orgPlanYearId: number;
  benefitPlans?: BenefitPlanDto[];
}

export const Contribution = ({
  benefit,
  plan,
  coverage,
  updateElection,
  selectedDependentIds,
  showEsignatureModal,
  orgPlanYearId,
  benefitPlans,
}: ContributionProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('pages.financial');
  const [isPerPayPeriod, setisPerPayPeriod] = useState(true);
  // allows for min/max values to be obtained if there is no coverage yet
  const [firstLoad, setFirstLoad] = useState(
    coverage && coverage.req_coverage_level_lookup_code !== 'decline'
      ? false
      : true
  );
  // per pay period contribution value
  const [eeContributionPPP, setEeContributionPPP] = useState(
    coverage ? coverage.req_subscriber_premium : 0
  );
  // annual / monthly contribution value
  const [eeContributionAnnual, setEeContributionAnnual] = useState(
    coverage ? Number(coverage.req_subscriber_fsa_amount) : 0
  );
  const [contributionEndDate, setContributionEndDate] = useState('');
  const [eeLumpSumAmount, setEeLumpSumAmount] = useState(0);
  const [electedRiders, setElectedRiders] = useState<number[]>([]);
  const perPayText = useSelector(selectPerPayPeriodText);
  const enrollmentContextType = useSelector(selectEnrollmentContext);
  const lifeEventCompleted = useSelector(selectLifeEventCompleted);

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(lumosApiHost),
  });
  const isHSANewExperience = isHSANewExperienceEnabled(
    companySettings,
    benefit,
    enrollmentContextType
  );

  const enrollmentMethod = benefit.enrollment_method || ANNUAL_METHOD;

  const emptyFunction = () => {
    return;
  };

  const benefitsPlanData = benefitPlans?.find(
    (bPlan) => plan.plan_id === bPlan.plan_id
  );

  const electPlan = (additionalPlanData?: AdditionalPlanElectionDataDto) => {
    updateElection(
      buildElectionParams({
        plan,
        levelId: benefitsPlanData?.level_id,
        orgBenefitId: benefit.org_benefit_id,
        enrollmentContextType,
        contribution: eeContributionAnnual,
        eeLumpSum: eeLumpSumAmount,
        additionalPlanData,
        selectedDependentIds: selectedDependentIds,
        electedRiders,
      })
    );
  };

  const handleHsaSubmission = (hsaData: HSAContributionsResponseDto) => {
    electPlan({
      v3_hsa_data: hsaData,
    });
  };

  const [resetHsaCalculations, setResetHsaCalculations] = useState(false);

  const getQueryValues = () => {
    const coverageLevelId = plan.level_id;
    const orgPlanId = plan.plan_id;
    const factorYtdIntoCalculatedMinMax = false;
    const orgBenefitId = benefit.org_benefit_id;
    const contribution = isPerPayPeriod
      ? eeContributionPPP
      : eeContributionAnnual;
    const electionAmount = firstLoad ? undefined : contribution;
    const contributionAmountType = isPerPayPeriod
      ? 'enter_pay_period'
      : enrollmentMethod;

    return {
      initLoad: firstLoad,
      lumosApiHost: String(lumosApiHost),
      orgBenefitId: orgBenefitId,
      coverageLevelId: coverageLevelId,
      enrollmentContextType: enrollmentContextType,
      electionAmount: electionAmount,
      contributionAmountType: contributionAmountType,
      orgPlanId: orgPlanId,
      lifeEventCompleted: lifeEventCompleted,
      factorYtdIntoCalculatedMinMax: factorYtdIntoCalculatedMinMax,
      resetToInitialCalculatedAmount: false,
    };
  };

  //useContributionAmountQuery caches params so we need to force update them when the values change
  const [queryParams, setQueryParams] = useState(() => getQueryValues());

  const v3ContributionQuery = useV3ContributionAmountQuery(queryParams, {
    enabled: isHSANewExperience,
  });

  const contributionQuery = useContributionAmountQuery(queryParams, {
    enabled: !isHSANewExperience,
  });

  const {
    contributionData,
    isLoadingContribution,
    isFetchingContribution,
    refetchContribution,
  } = isHSANewExperience ? v3ContributionQuery : contributionQuery;

  const isLoading = isLoadingContribution || isFetchingContribution;

  useEffect(() => {
    if (
      isHSANewExperience &&
      contributionData &&
      contributionData?.data &&
      !isLoading
    ) {
      if (resetHsaCalculations) {
        dispatch(resetHsaInitalResposne(contributionData?.data));
      } else {
        dispatch(updateHSAContributionResponse(contributionData?.data));
      }
      setResetHsaCalculations(false);
    }
  }, [contributionData, isLoading]);

  const getNewResponse = () => {
    const radios = document.getElementsByName(
      `radio-contribution-benefit-${plan.plan_id}`
    );
    const btn = document.getElementById(
      `maximize-${plan.plan_id}`
    ) as HTMLElement;
    const input = document.getElementById(
      `personal-contribution-${plan.plan_id}`
    ) as HTMLElement;
    if (input && btn && radios) {
      const queryValues = getQueryValues();
      setQueryParams(queryValues);
      disableFields(input, btn, radios);
    }
  };

  useEffect(() => {
    if (queryParams) {
      refetchContribution();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  useEffect(() => {
    getNewResponse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPerPayPeriod, eeContributionAnnual, firstLoad]);

  useEffect(() => {
    const radios = document.getElementsByName(
      `radio-contribution-benefit-${plan.plan_id}`
    );
    const btn = document.getElementById(
      `maximize-${plan.plan_id}`
    ) as HTMLElement;
    const input = document.getElementById(
      `personal-contribution-${plan.plan_id}`
    ) as HTMLElement;
    if (input && btn && radios) {
      enableFields(input, btn, radios);
    }
  });

  const disableFields = (
    input: HTMLElement,
    btn: HTMLElement,
    radios: NodeListOf<HTMLElement>
  ) => {
    input.setAttribute('disabled', 'true');
    btn.setAttribute('disabled', 'true');
    radios.forEach((radio) => radio.setAttribute('disabled', 'true'));
  };

  const enableFields = (
    input: HTMLElement,
    btn: HTMLElement,
    radios: NodeListOf<HTMLElement>
  ) => {
    input.removeAttribute('disabled');
    btn.removeAttribute('disabled');
    radios.forEach((radio) => radio.removeAttribute('disabled'));
  };

  const handleAmountTypeChange = (perPeriod: boolean) => {
    if (isPerPayPeriod === perPeriod) {
      return;
    }
    setisPerPayPeriod(perPeriod);
    const pppRadio = document.getElementById(
      `contribution-benefit-per-period-${plan.plan_id}`
    );
    const annualRadio = document.getElementById(
      `contribution-benefit-annual-${plan.plan_id}`
    );

    if (pppRadio && annualRadio) {
      if (perPeriod) {
        annualRadio.removeAttribute('checked');
        pppRadio.setAttribute('checked', 'true');
      } else {
        pppRadio.removeAttribute('checked');
        annualRadio.setAttribute('checked', 'true');
      }
    }
  };

  useEffect(() => {
    if (contributionData && firstLoad) {
      setFirstLoad(false);
      const min = contributionData.data.calculated_subscriber_min;
      const max = contributionData.data.calculated_subscriber_max;
      const remainingPaydays =
        contributionData.data.remaining_paydays_until_contribution_end;
      const remainingMonths =
        contributionData.data.remaining_months_until_contribution_end;

      if (isPerPayPeriod) {
        const value = valueWithinBounds(eeContributionPPP, min, max);
        setEeContributionPPP(value);
        const amountAnnual =
          enrollmentMethod === ANNUAL_METHOD
            ? Number(value * remainingPaydays)
            : Number(value * (remainingPaydays / remainingMonths));
        setEeContributionAnnual(amountAnnual);
      } else {
        const value = valueWithinBounds(eeContributionAnnual, min, max);
        setEeContributionAnnual(value);
        const amountPpp = Number(value / remainingPaydays);
        setEeContributionPPP(amountPpp);
      }
      setEeLumpSumAmount(
        contributionData?.data.ee_lump_sum_contribution_amount || 0
      );
    }
  }, [
    contributionData,
    firstLoad,
    eeContributionPPP,
    eeContributionAnnual,
    isPerPayPeriod,
    enrollmentMethod,
  ]);

  const resetHsaCalculationAmountValues = (data: boolean) => {
    if (data && isHSANewExperience) {
      setResetHsaCalculations(data);
      const queryValues = getQueryValues();
      const queryValuesData = {
        ...queryValues,
        resetToInitialCalculatedAmount: true,
      };
      setQueryParams(queryValuesData);
    }
  };

  return (
    <div className="card p-0">
      {isLoading && <LoaderOverlay />}
      <PlanCardHeaderSyled>
        <div className="icon-container annotation2">
          <PiggyBank size={20} color="white" />
        </div>
        <h3 className="pl-3_5 m-0">{plan.plan_name}</h3>
        <CarrierLogoBoxStyled>
          {plan.carrier_logo_url ? (
            <img
              src={plan.carrier_logo_url}
              alt={plan.carrier || t('defaultLogoAltText')}
              data-testid="carrier-logo"
              style={{ maxHeight: '100%' }}
            />
          ) : (
            <h3 className="m-0">{plan.carrier}</h3>
          )}
        </CarrierLogoBoxStyled>
      </PlanCardHeaderSyled>
      {/* body content */}
      {isHSANewExperience ? (
        <HsaContribution
          contributionData={contributionData}
          onHsaSubmitted={handleHsaSubmission}
          showEsignatureModal={showEsignatureModal}
          plan={plan}
          resetHsaCalculationValues={resetHsaCalculationAmountValues}
        />
      ) : (
        <div className="px-3_5 px-md-5">
          <DecisionIQFinancialBanner
            planId={plan.plan_id}
            isHsaPlan={benefit.benefit_lookup_code === 'hsa'}
            orgBenefitId={benefit.org_benefit_id}
            setEeContributionPPP={setEeContributionPPP}
            setEeContributionAnnual={setEeContributionAnnual}
            subscriberMax={contributionData?.data.subscriber_max_amount || 0}
            numPayPeriods={
              contributionData?.data.remaining_paydays_until_contribution_end ||
              0
            }
          />

          <div className="row ml-0 mt-3" data-testid="contribution-header">
            <h2 className="mb-0">{t('selectContribution')}</h2>
          </div>
          <fieldset className="row ml-0" role="radiogroup">
            <legend className="sr-only">
              {t('newHSA.contributionFrequency')}
            </legend>
            <div className="mr-4">
              <PsRadioTuner
                key="contribution-benefit"
                inputId={`contribution-benefit-per-period-${plan.plan_id}`}
                data-testid={`contribution-benefit-per-period-${plan.plan_id}`}
                name={`radio-contribution-benefit-${plan.plan_id}`}
                label={`${perPayText}`}
                checked
                onClick={() => handleAmountTypeChange(true)}
              ></PsRadioTuner>
            </div>
            <PsRadioTuner
              key="contribution-benefit"
              inputId={`contribution-benefit-annual-${plan.plan_id}`}
              data-testid={`contribution-benefit-annual-${plan.plan_id}`}
              name={`radio-contribution-benefit-${plan.plan_id}`}
              label={t(enrollmentMethod)}
              onClick={() => handleAmountTypeChange(false)}
            ></PsRadioTuner>
          </fieldset>
          {/* Need to gate component behind valid data to set static values
         on load for orbit components */}
          {contributionData && (
            <ContributionElection
              eeContributionPPP={eeContributionPPP}
              eeContributionAnnual={eeContributionAnnual}
              isPerPayPeriod={isPerPayPeriod}
              enrollmentMethod={enrollmentMethod}
              setEeContributionPPP={setEeContributionPPP}
              setEeContributionAnnual={setEeContributionAnnual}
              contributionData={
                contributionData as ContributionAmountResponseDto
              }
              contributionEndDate={contributionEndDate}
              setContributionEndDate={setContributionEndDate}
              eeLumpSumAmount={eeLumpSumAmount}
              setEeLumpSumAmount={setEeLumpSumAmount}
              isLoading={isLoading}
              planId={plan.plan_id}
            />
          )}
        </div>
      )}

      <RiderBenefits
        riderInfo={plan.org_plan_rider_benefits as PlanRiderDto[]}
        riderCoverages={coverage?.rider_coverages || []}
        planPremium={isPerPayPeriod ? eeContributionPPP : eeContributionAnnual}
        electedRiders={electedRiders}
        setElectedRiders={setElectedRiders}
        orgPlanYearId={orgPlanYearId}
        displayCheckbox
        smallMobileMargins={true}
      />

      {!isHSANewExperience ? (
        <div className="border-top px-5 py-4 text-center text-md-left">
          {showEsignatureModal ? (
            <EsignatureModal
              isFinancial={true}
              setEsignElectionData={electPlan}
              carrierLogoUrl={plan?.carrier_logo_url}
              carrierName={plan?.carrier}
              data={plan?.esign_data}
              planId={plan?.plan_id}
              setModalRequired={emptyFunction}
            />
          ) : (
            <ps-button
              id={`submit-contribution-${plan.plan_id}`}
              level="primary"
              label={t('looksGood')}
              disabled={true}
              onClick={() => electPlan()}
            ></ps-button>
          )}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};
